import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Handle, Position } from "@xyflow/react";
import FormFlowService from "../../services/FormFlowService";

export const FlowMessageNode = ({ data, id, selected = false }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const menuRef = useRef(null);

  const [body, setBody] = useState(data.body || "");
  const [buttonLabel, setButtonLabel] = useState(data.buttonLabel || "Open Form");
  const [answerVariable, setAnswerVariable] = useState(data.answerVariable || "");
  const [flowId, setFlowId] = useState(data.flowId || "");
  const [flowName, setFlowName] = useState(data.flowName || "");
  const [handlerType, setHandlerType] = useState(data.handlerType || "flow");

  const [editBody, setEditBody] = useState(data.body || "");
  const [editButtonLabel, setEditButtonLabel] = useState(data.buttonLabel || "Open Form");
  const [editAnswerVariable, setEditAnswerVariable] = useState(data.answerVariable || "");
  const [editFlowId, setEditFlowId] = useState(data.flowId || "");
  const [editFlowName, setEditFlowName] = useState(data.flowName || "");
  const [editHandlerType, setEditHandlerType] = useState(data.handlerType || "flow");

  const [forms, setForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);

  const [bodyError, setBodyError] = useState("");
  const [buttonError, setButtonError] = useState("");
  const [answerVariableError, setAnswerVariableError] = useState("");
  const [flowError, setFlowError] = useState("");

  useEffect(() => {
    setBody(data.body || "");
    setButtonLabel(data.buttonLabel || "Open Form");
    setAnswerVariable(data.answerVariable || "");
    setFlowId(data.flowId || "");
    setFlowName(data.flowName || "");
    setHandlerType(data.handlerType || "flow");
  }, [data.body, data.buttonLabel, data.answerVariable, data.flowId, data.flowName, data.handlerType]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const openEdit = async () => {
    setEditBody(body || "");
    setEditButtonLabel(buttonLabel || "Open Form");
    setEditAnswerVariable(answerVariable || "");
    setEditFlowId(flowId || "");
    setEditFlowName(flowName || "");
    setEditHandlerType(handlerType || "flow");
    setBodyError("");
    setButtonError("");
    setAnswerVariableError("");
    setFlowError("");
    setShowEditPopup(true);
    setShowMenu(false);

    try {
      setLoadingForms(true);
      const allForms = await FormFlowService.listForms();
      const published = (allForms || []).filter(
        (f) => String(f?.status || "").toLowerCase() === "published",
      );
      setForms(published.length ? published : allForms || []);
    } catch (e) {
      setForms([]);
    } finally {
      setLoadingForms(false);
    }
  };

  const handleDelete = () => {
    if (data.onDelete) data.onDelete(id);
    setShowMenu(false);
  };

  const validateAnswerVariable = (value) => {
    if (!value) {
      setAnswerVariableError("");
      return true;
    }
    const isValid = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value);
    if (!isValid) {
      setAnswerVariableError("Use letters, numbers, underscore; cannot start with number");
      return false;
    }
    setAnswerVariableError("");
    return true;
  };

  const handleSave = () => {
    let hasError = false;
    if (!editBody.trim()) {
      setBodyError("Body is required");
      hasError = true;
    } else {
      setBodyError("");
    }
    if (!editButtonLabel.trim()) {
      setButtonError("Button label is required");
      hasError = true;
    } else {
      setButtonError("");
    }
    if (!editFlowId) {
      setFlowError("Please select a published flow");
      hasError = true;
    } else {
      setFlowError("");
    }
    if (!validateAnswerVariable(editAnswerVariable)) {
      hasError = true;
    }
    if (hasError) return;

    if (data.onChangeBody) data.onChangeBody(id, editBody);
    if (data.onChangeButtonLabel) data.onChangeButtonLabel(id, editButtonLabel);
    if (data.onChangeAnswerVariable) data.onChangeAnswerVariable(id, editAnswerVariable || "");
    if (data.onChangeFlowId) data.onChangeFlowId(id, editFlowId);
    if (data.onChangeFlowName) data.onChangeFlowName(id, editFlowName || "");
    if (data.onChangeHandlerType) data.onChangeHandlerType(id, editHandlerType);

    setBody(editBody);
    setButtonLabel(editButtonLabel);
    setAnswerVariable(editAnswerVariable || "");
    setFlowId(editFlowId);
    setFlowName(editFlowName || "");
    setHandlerType(editHandlerType);
    setShowEditPopup(false);
  };

  const handleCancel = () => {
    setShowEditPopup(false);
    setShowMenu(false);
  };

  const popup = (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.35)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 10000,
      }}
      onClick={handleCancel}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "10px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          width: "560px",
          maxWidth: "90vw",
          maxHeight: "88vh",
          overflow: "auto",
          padding: "16px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>Edit Flow Message</h3>

        <div style={{ marginBottom: 10 }}>
          <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>Flow</label>
          <select
            value={editFlowId}
            onChange={(e) => {
              const selected = forms.find(
                (f) => String(f.meta_flow_id || f.id) === String(e.target.value),
              );
              setEditFlowId(e.target.value);
              setEditFlowName(selected?.name || "");
              setFlowError("");
            }}
            style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          >
            <option value="">{loadingForms ? "Loading..." : "Select published flow"}</option>
            {forms.map((f) => (
              <option key={f.id} value={f.meta_flow_id || f.id}>
                {f.name} ({f.meta_flow_id || f.id})
              </option>
            ))}
          </select>
          {flowError ? <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 4 }}>{flowError}</div> : null}
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>Body</label>
          <textarea
            value={editBody}
            onChange={(e) => {
              setEditBody(e.target.value);
              setBodyError("");
            }}
            rows={4}
            maxLength={1024}
            style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #ccc", resize: "vertical" }}
          />
          {bodyError ? <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 4 }}>{bodyError}</div> : null}
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>Button label</label>
          <input
            type="text"
            value={editButtonLabel}
            onChange={(e) => {
              setEditButtonLabel(e.target.value);
              setButtonError("");
            }}
            maxLength={20}
            style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          />
          {buttonError ? <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 4 }}>{buttonError}</div> : null}
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>Node type</label>
          <select
            value={editHandlerType}
            onChange={(e) => setEditHandlerType(e.target.value)}
            style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          >
            <option value="flow">Flow</option>
            <option value="order">Order</option>
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>Answer variable</label>
          <input
            type="text"
            value={editAnswerVariable}
            onChange={(e) => {
              setEditAnswerVariable(e.target.value);
              validateAnswerVariable(e.target.value);
            }}
            placeholder="e.g. address_form"
            style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          />
          {answerVariableError ? <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 4 }}>{answerVariableError}</div> : null}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={handleCancel} style={{ padding: "8px 12px" }}>Cancel</button>
          <button type="button" onClick={handleSave} style={{ padding: "8px 12px", background: "#1976d2", color: "#fff", border: "none", borderRadius: 6 }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        style={{
          background: selected ? "#f8f9fa" : "#f1f3f4",
          border: `1px solid ${selected ? "#1976d2" : "#e0e0e0"}`,
          borderRadius: 6,
          padding: 8,
          width: 190,
          minHeight: 82,
          boxShadow: selected ? "0 4px 8px rgba(25, 118, 210, 0.3)" : "0 2px 4px rgba(0,0,0,0.1)",
          position: "relative",
        }}
      >
        <div
          style={{ position: "absolute", top: 10, right: 8, cursor: "pointer", fontSize: 16, color: selected ? "#1976d2" : "#6c757d", fontWeight: "bold" }}
          onClick={() => setShowMenu(!showMenu)}
        >
          ⋮
        </div>

        {showMenu && (
          <div ref={menuRef} style={{ position: "absolute", top: 24, right: 8, background: "white", border: "1px solid #ddd", borderRadius: 4, boxShadow: "0 2px 8px rgba(0,0,0,0.15)", zIndex: 2 }}>
            <div style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12, borderBottom: "1px solid #eee" }} onClick={openEdit}>✏️ Edit</div>
            <div style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12 }} onClick={handleDelete}>🗑️ Delete</div>
          </div>
        )}

        <Handle type="target" position={Position.Left} style={{ background: selected ? "#1976d2" : "#6c757d" }} />
        <Handle type="source" position={Position.Right} id={`${id}-main`} style={{ background: selected ? "#1976d2" : "#6c757d" }} />

        <div style={{ textAlign: "left" }}>
          <div style={{ fontWeight: "bold", color: selected ? "#1976d2" : "#6c757d", marginBottom: 6, fontSize: 11 }}>
            Flow Message
          </div>
          <div style={{ fontSize: 12, color: "#444", marginBottom: 6, whiteSpace: "pre-wrap" }}>
            {(body || "Click Edit to configure flow message").slice(0, 90)}
          </div>
          <div style={{ background: "#0d6efd", color: "white", fontSize: 10, padding: "4px 8px", borderRadius: 4, textAlign: "center", marginBottom: 6 }}>
            {buttonLabel || "Open Form"}
          </div>
          <div style={{ fontSize: 10, color: "#555" }}>
            {flowName ? `Flow: ${flowName}` : "Flow: not selected"}
          </div>
          <div style={{ marginTop: 4, fontSize: 10, color: "#64748b" }}>
            Type: {handlerType === "order" ? "Order" : "Flow"}
          </div>
          {flowId ? (
            <div
              style={{
                marginTop: 4,
                display: "inline-block",
                fontSize: 10,
                color: "#166534",
                background: "#dcfce7",
                border: "1px solid #86efac",
                borderRadius: 999,
                padding: "2px 8px",
              }}
            >
              Selected: {flowId}
            </div>
          ) : (
            <div
              style={{
                marginTop: 4,
                fontSize: 10,
                color: "#b91c1c",
                background: "#fee2e2",
                border: "1px solid #fecaca",
                borderRadius: 6,
                padding: "4px 6px",
              }}
            >
              Select a published flow in Edit
            </div>
          )}
          {answerVariable ? (
            <div style={{ fontSize: 10, color: "#666", marginTop: 3 }}>Var: {answerVariable}</div>
          ) : null}
        </div>
      </div>
      {showEditPopup ? createPortal(popup, document.body) : null}
    </>
  );
};

